import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Pagination, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { Hub, ManageAccounts, OpenInNew, Share } from '@mui/icons-material';
import companyAdminService from '../../services/companyAdminService';
import resourceSharingService from '../../services/resourceSharingService';
import ResourceShareDialog from '../shared/ResourceShareDialog';
import ManageSharingDialog from '../shared/ManageSharingDialog';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import { SHARING_COPY } from '../../constants/partnerCopy';
import { describeApiError } from '../../utils/apiError';

const resourceTypes = [
  { value: 'agent', label: 'Agents' }, { value: 'folder_dim1', label: 'Rules book (D1)' },
  { value: 'folder_dim3', label: 'Customer Data (D3)' }, { value: 'composio_app', label: 'Integrations' },
  { value: 'custom_tool', label: 'Custom tools' },
];
const PAGE_SIZE = 25;
const getName = (item) => item?.resource?.name || item?.resource?.app_name || item?.resource?.tool_key || `Resource ${item?.resource_id}`;
const ownerName = (owner) => owner?.name || [owner?.first_name, owner?.last_name].filter(Boolean).join(' ') || owner?.email || 'Unknown owner';

const CompanyResourcesPanel = ({ notify }) => {
  const { has, ready: permissionsReady, partner } = usePermissions();
  const canLoadOwnerDirectory = permissionsReady && has(PERMISSIONS.USER_MANAGEMENT);
  const [resourceType, setResourceType] = useState('agent');
  const [ownerId, setOwnerId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shareResource, setShareResource] = useState(null);
  const [manageResource, setManageResource] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  useEffect(() => { const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350); return () => clearTimeout(timer); }, [search]);
  useEffect(() => {
    if (!canLoadOwnerDirectory) {
      setUsers([]);
      return;
    }
    companyAdminService.listUsers({ limit: 200, offset: 0 }).then((data) => setUsers(Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : [])).catch(() => setUsers([]));
  }, [canLoadOwnerDirectory]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const result = await resourceSharingService.getCompanyResources({ resourceType, userId: ownerId, search: debouncedSearch, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
      setItems(Array.isArray(result?.items) ? result.items : []); setTotal(Number(result?.total) || 0);
    } catch (err) { setError(describeApiError(err, 'Could not load company resources.')); }
    finally { setLoading(false); }
  }, [resourceType, ownerId, debouncedSearch, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [resourceType, ownerId, debouncedSearch]);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const typeLabel = useMemo(() => resourceTypes.find((type) => type.value === resourceType)?.label || 'Resources', [resourceType]);
  const dialogResource = (item) => ({ type: item.resource_type, id: item.resource_id, name: getName(item) });

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', mb: 2.5 }}>
        <Box><Typography variant="h5" sx={{ fontWeight: 750 }}>Company Resources</Typography><Typography variant="body2" color="text.secondary">Browse and share resources created by anyone in your company. Editing and deletion remain with the owner.</Typography></Box>
        <Chip label={`${total} ${typeLabel.toLowerCase()}`} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 240px minmax(240px, 1fr)' }, gap: 1.5, mb: 2 }}>
        <FormControl size="small"><InputLabel>Resource type</InputLabel><Select label="Resource type" value={resourceType} onChange={(e) => setResourceType(e.target.value)}>{resourceTypes.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}</Select></FormControl>
        <FormControl size="small" disabled={!canLoadOwnerDirectory}><InputLabel>Owner</InputLabel><Select label="Owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}><MenuItem value="">All owners</MenuItem>{users.map((user) => <MenuItem key={user.id} value={user.id}>{ownerName(user)}</MenuItem>)}</Select></FormControl>
        <TextField size="small" label="Search resources" value={search} onChange={(e) => setSearch(e.target.value)} />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper' }}>
        <Table><TableHead><TableRow><TableCell>Resource</TableCell><TableCell>Owner</TableCell><TableCell>Sharing</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={4} align="center" sx={{ py: 8 }}><CircularProgress size={28} /></TableCell></TableRow> : items.length === 0 ? <TableRow><TableCell colSpan={4} align="center" sx={{ py: 8, color: 'text.secondary' }}>No company resources match these filters.</TableCell></TableRow> : items.map((item) => (
              <TableRow key={`${item.resource_type}-${item.resource_id}`} hover>
                <TableCell><Typography sx={{ fontWeight: 700 }}>{getName(item)}</Typography><Typography variant="caption" color="text.secondary">{resourceTypes.find((type) => type.value === item.resource_type)?.label}</Typography></TableCell>
                <TableCell><Typography variant="body2" sx={{ opacity: item.owner?.is_active === false ? 0.55 : 1 }}>{ownerName(item.owner)}</Typography><Typography variant="caption" color="text.secondary">{item.owner?.email}</Typography></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`${item.share_count || 0} audience${Number(item.share_count) === 1 ? '' : 's'}`} />
                    {/* Your partner shared this across its companies (§7.3) — you can see who has access and revoke it. */}
                    {Number(item.partner_share_count) > 0 && (
                      <Tooltip title={SHARING_COPY.ownerCompanyBadgeTooltip} arrow>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          icon={<Hub sx={{ fontSize: 16 }} />}
                          label={SHARING_COPY.ownerCompanyBadge(partner?.name, item.partner_share_count)}
                          onClick={() => setManageResource(dialogResource(item))}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right"><Button size="small" startIcon={<OpenInNew />} onClick={() => setViewItem(item)}>View</Button><Button size="small" startIcon={<Share />} onClick={() => setShareResource(dialogResource(item))}>Share</Button><Button size="small" startIcon={<ManageAccounts />} onClick={() => setManageResource(dialogResource(item))}>Manage</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pageCount > 1 && <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }} />}
      <ResourceShareDialog open={Boolean(shareResource)} resource={shareResource} onClose={() => setShareResource(null)} onSuccess={(result) => { const warnings = Array.isArray(result?.warnings) ? result.warnings : []; notify?.(warnings.length ? `Shared successfully. ${warnings.join(' ')}` : 'Resource shared successfully.', warnings.length ? 'warning' : 'success'); load(); }} />
      <ManageSharingDialog open={Boolean(manageResource)} resource={manageResource} onClose={() => setManageResource(null)} onChanged={load} />
      <Dialog open={Boolean(viewItem)} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Resource details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" sx={{ fontWeight: 750 }}>{viewItem ? getName(viewItem) : ''}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Owned by {ownerName(viewItem?.owner)} {viewItem?.owner?.email ? `(${viewItem.owner.email})` : ''}
          </Typography>
          <Box sx={{ display: 'grid', gap: 1 }}>
            {Object.entries(viewItem?.resource || {}).filter(([key]) => !['id', 'name', 'app_name'].includes(key)).map(([key, value]) => (
              <Box key={key} sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{key.replaceAll('_', ' ')}</Typography>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{value === null || value === undefined || value === '' ? '—' : String(value)}</Typography>
              </Box>
            ))}
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>This company view is read-only. Editing and deletion remain with the resource owner.</Alert>
        </DialogContent>
        <DialogActions><Button onClick={() => setViewItem(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyResourcesPanel;
